export const IRAS_TABLE_SPACING_PRESETS = {
  default: {
    sectionSpacerRow: null,
    tableStyles: {
      cellPadding: { top: 1.76, bottom: 1.76, left: 1.76, right: 1.76 },
      fontSize: 10,
      minCellHeight: 0,
    },
  },
  compact: {
    sectionSpacerRow: {
      cellPadding: 0,
      fontSize: 1,
      minCellHeight: 2,
    },
    tableStyles: {
      cellPadding: { top: 0.75, bottom: 0.75, left: 1, right: 1 },
      fontSize: 10,
      minCellHeight: 3.8,
    },
  },
};

export const IRAS_TABLE_SPACING_DISPLAY_PRESETS = {
  default: {
    ...IRAS_TABLE_SPACING_PRESETS.default,
    sectionSpacerRow: {
      cellPadding: 0,
      fontSize: 1,
      minCellHeight: 0,
    },
  },
  compact: IRAS_TABLE_SPACING_PRESETS.compact,
};

const mergeObject = (base, override) => {
  if (override === undefined) return base;
  if (base === undefined || base === null) return override;
  if (
    typeof base !== "object" ||
    typeof override !== "object" ||
    Array.isArray(base) ||
    Array.isArray(override)
  ) {
    return override;
  }

  return {
    ...base,
    ...override,
  };
};

export const resolveIrasTableSpacingConfig = (options = false) => {
  if (options && options.resolved === true) return options;

  const normalised =
    options && typeof options === "object"
      ? options
      : { density: options ? "compact" : "default" };
  const {
    density = "default",
    mode,
    overrides: explicitOverrides,
    ...directOverrides
  } = normalised;
  const preset =
    IRAS_TABLE_SPACING_PRESETS[mode || density] ||
    IRAS_TABLE_SPACING_PRESETS.default;
  const overrides = {
    ...directOverrides,
    ...(explicitOverrides || {}),
  };

  return {
    resolved: true,
    sectionSpacerRow:
      overrides.sectionSpacerRow === null
        ? null
        : mergeObject(preset.sectionSpacerRow, overrides.sectionSpacerRow),
    tableStyles: {
      ...preset.tableStyles,
      ...overrides.tableStyles,
      cellPadding: mergeObject(
        preset.tableStyles?.cellPadding,
        overrides.tableStyles?.cellPadding
      ),
    },
  };
};

export const getIrasTableStyles = (pdf, spacingOptions = false) => ({
  fontSize: pdf.fs,
  ...resolveIrasTableSpacingConfig(spacingOptions).tableStyles,
});

export const getIrasNoteTableOptions = (pdf, spacingOptions = false) => {
  const tableStyles = getIrasTableStyles(pdf, spacingOptions);
  const { cellPadding, fontSize, minCellHeight } = tableStyles;

  return {
    bodyStyles: {
      ...(cellPadding ? { cellPadding } : {}),
      ...(minCellHeight ? { minCellHeight } : {}),
    },
    styles: {
      ...(fontSize ? { fontSize } : {}),
    },
  };
};

export const getIrasTableCellPadding = (spacingOptions = false) =>
  resolveIrasTableSpacingConfig(spacingOptions).tableStyles?.cellPadding;

export const normaliseIrasAutoTextColumnSpacing = (
  body,
  columnIndex,
  spacingOptions = false
) => {
  const config = resolveIrasTableSpacingConfig(spacingOptions);
  const cellPadding = config.tableStyles?.cellPadding;
  const minCellHeight = config.tableStyles?.minCellHeight;
  if (!cellPadding && !minCellHeight) return;

  body.forEach((row) => {
    const cell = row?.[columnIndex];
    if (!cell || typeof cell !== "object") return;

    cell.styles = {
      ...cell.styles,
      ...(cellPadding ? { cellPadding } : {}),
      ...(minCellHeight ? { minCellHeight } : {}),
    };
  });
};

export const buildIrasSpacerRow = (columnCount, spacingOptions = true) => {
  const config = resolveIrasTableSpacingConfig(spacingOptions);
  const styles =
    config.sectionSpacerRow ||
    IRAS_TABLE_SPACING_PRESETS.compact.sectionSpacerRow;

  return [
    {
      __isIrasSpacer: true,
      colSpan: columnCount,
      content: "",
      styles,
    },
  ];
};

export const buildIrasSectionSpacer = (columnCount, spacingOptions = false) => {
  const config = resolveIrasTableSpacingConfig(spacingOptions);
  if (!config.sectionSpacerRow) return [];

  return buildIrasSpacerRow(columnCount, config);
};

export const isIrasSpacerRow = (row) => Boolean(row?.[0]?.__isIrasSpacer);
